import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowRight, Truck, Shield, Headphones } from "lucide-react";
import { useApp } from "../context/AppContext";
import ProductCard from "../components/ProductCard";
import { Button } from "../components/ui/button";

const HomePage = () => {
  const { categories, API } = useApp();
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [latestProducts, setLatestProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const [featuredRes, latestRes] = await Promise.all([
          axios.get(`${API}/products?featured=true&limit=8`),
          axios.get(`${API}/products?limit=12`),
        ]);
        setFeaturedProducts(featuredRes.data);
        setLatestProducts(latestRes.data);
      } catch (error) {
        console.error("Failed to fetch products:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, [API]);

  // Seed data on first load if empty
  useEffect(() => {
    const seedData = async () => {
      try {
        await axios.post(`${API}/seed`);
        await axios.post(`${API}/admin/setup`);
        // Refetch after seeding
        const [featuredRes, latestRes] = await Promise.all([
          axios.get(`${API}/products?featured=true&limit=8`),
          axios.get(`${API}/products?limit=12`),
        ]);
        setFeaturedProducts(featuredRes.data);
        setLatestProducts(latestRes.data);
      } catch (error) {
        // Already seeded, ignore
      }
    };
    
    if (!loading && latestProducts.length === 0) {
      seedData();
    }
  }, [loading, latestProducts.length, API]);

  return (
    <div className="min-h-screen" data-testid="homepage">
      {/* Hero Section */}
      <section className="relative h-[500px] md:h-[600px] overflow-hidden" data-testid="hero-section">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://static.prod-images.emergentagent.com/jobs/c6028fcd-9387-457c-8442-b6bda09a941d/images/f3be645599f6fc0ab839ff0aac1489eb821dc8e93c97df91e18420ed9b3e3cc3.png')`
          }}
        />
        <div className="hero-overlay absolute inset-0" />
        <div className="relative z-10 container mx-auto px-4 md:px-6 lg:px-8 h-full flex items-center">
          <div className="max-w-xl text-white">
            <p className="bazaar-badge text-white/80 mb-4 animate-fade-in-up">Welcome to</p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 animate-fade-in-up stagger-1">
              Mini Begum<br />
              <span className="text-accent">Bazaar</span>
            </h1>
            <p className="text-lg md:text-xl text-white/90 mb-8 animate-fade-in-up stagger-2">
              Your one-stop destination for fancy items, fashion, electronics, groceries, and everything you need!
            </p>
            <div className="flex flex-wrap gap-4 animate-fade-in-up stagger-3">
              <Link to="/categories">
                <Button size="lg" className="bg-primary hover:bg-primary/90 text-white" data-testid="hero-shop-now">
                  Shop Now
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <Link to="/categories">
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-foreground" data-testid="hero-explore">
                  Explore Categories
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-8 bg-white border-b" data-testid="features-section">
        <div className="container mx-auto px-4 md:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="flex items-center gap-4 p-4">
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                <Truck className="w-6 h-6 text-primary" />
              </div>
              <div>
                <h4 className="font-semibold text-foreground">Fast Delivery</h4>
                <p className="text-sm text-muted-foreground">Free delivery on orders above ₹500</p>
              </div>
            </div>
            <div className="flex items-center gap-4 p-4">
              <div className="w-12 h-12 rounded-full bg-accent/10 flex items-center justify-center">
                <Shield className="w-6 h-6 text-accent" />
              </div>
              <div>
                <h4 className="font-semibold text-foreground">Secure Payment</h4>
                <p className="text-sm text-muted-foreground">100% secure payment with Razorpay</p>
              </div>
            </div>
            <div className="flex items-center gap-4 p-4">
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                <Headphones className="w-6 h-6 text-primary" />
              </div>
              <div>
                <h4 className="font-semibold text-foreground">24/7 Support</h4>
                <p className="text-sm text-muted-foreground">Customer support available always</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Categories Bento Grid */}
      <section className="py-12 md:py-16" data-testid="categories-section">
        <div className="container mx-auto px-4 md:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-8">
            <div>
              <p className="bazaar-badge text-primary mb-2">Browse</p>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-foreground">Shop by Category</h2>
            </div>
            <Link to="/categories" className="hidden md:flex items-center gap-2 text-primary hover:underline font-medium" data-testid="view-all-categories">
              View All
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {categories.slice(0, 6).map((category, index) => (
              <Link
                key={category.id}
                to={`/category/${category.slug}`}
                className={`category-card overflow-hidden ${
                  index === 0 ? "col-span-2 row-span-2 h-[400px]" : 
                  index === 1 || index === 2 ? "col-span-2 h-48" : "h-48"
                }`}
                data-testid={`home-category-${category.slug}`}
              >
                <img
                  src={category.image_url || "https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=500"}
                  alt={category.name}
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-500"
                />
                <div className="absolute inset-0 z-10 flex flex-col justify-end p-4 md:p-6">
                  <h3 className="text-lg md:text-xl font-bold text-white mb-1">{category.name}</h3>
                  <div className="flex items-center gap-2 text-white/90">
                    <span className="text-sm">Shop Now</span>
                    <ArrowRight className="w-4 h-4" />
                  </div>
                </div>
              </Link>
            ))}
          </div>

          <div className="mt-6 md:hidden text-center">
            <Link to="/categories">
              <Button variant="outline" data-testid="mobile-view-all-categories">
                View All Categories
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Featured Products */}
      {featuredProducts.length > 0 && (
        <section className="py-12 md:py-16 bg-white" data-testid="featured-products-section">
          <div className="container mx-auto px-4 md:px-6 lg:px-8">
            <div className="flex items-center justify-between mb-8">
              <div>
                <p className="bazaar-badge text-accent mb-2">Best Sellers</p>
                <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-foreground">Featured Products</h2>
              </div>
              <Link to="/categories" className="hidden md:flex items-center gap-2 text-primary hover:underline font-medium" data-testid="view-all-featured">
                View All
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>

            <div className="product-grid">
              {featuredProducts.map((product, index) => (
                <div key={product.id} className={`animate-fade-in-up stagger-${(index % 4) + 1}`}>
                  <ProductCard product={product} />
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Banner */}
      <section className="py-12 md:py-16" data-testid="promo-banner">
        <div className="container mx-auto px-4 md:px-6 lg:px-8">
          <div className="relative rounded-2xl overflow-hidden bg-gradient-to-r from-primary to-primary/80 p-8 md:p-12">
            <div className="relative z-10 max-w-lg">
              <p className="bazaar-badge text-white/80 mb-2">Limited Time Offer</p>
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                Get 20% Off on Your First Order!
              </h2>
              <p className="text-white/90 mb-6">
                Shop from our wide collection and enjoy amazing discounts.
              </p>
              <Link to="/categories">
                <Button size="lg" className="bg-white text-primary hover:bg-white/90" data-testid="promo-shop-now">
                  Shop Now
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
            </div>
            <div className="absolute right-0 top-0 bottom-0 w-1/2 opacity-10">
              <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC40Ij48cGF0aCBkPSJNMzYgMzRjMC0yLjIwOS0xLjc5MS00LTQtNHMtNCAxLjc5MS00IDQgMS43OTEgNCA0IDQgNC0xLjc5MSA0LTR6bTAtMTJjMC0yLjIwOS0xLjc5MS00LTQtNHMtNCAxLjc5MS00IDQgMS43OTEgNCA0IDQgNC0xLjc5MSA0LTR6Ii8+PC9nPjwvZz48L3N2Zz4=')] bg-repeat" />
            </div>
          </div>
        </div>
      </section>

      {/* Latest Products */}
      {latestProducts.length > 0 && (
        <section className="py-12 md:py-16 bg-white" data-testid="latest-products-section">
          <div className="container mx-auto px-4 md:px-6 lg:px-8">
            <div className="flex items-center justify-between mb-8">
              <div>
                <p className="bazaar-badge text-primary mb-2">New Arrivals</p>
                <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-foreground">Latest Products</h2>
              </div>
              <Link to="/categories" className="hidden md:flex items-center gap-2 text-primary hover:underline font-medium" data-testid="view-all-latest">
                View All
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>

            <div className="product-grid">
              {latestProducts.map((product, index) => (
                <div key={product.id} className={`animate-fade-in-up stagger-${(index % 4) + 1}`}>
                  <ProductCard product={product} />
                </div>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default HomePage;
